import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import {
  attemptTimestamp,
  fetchPracticeAttemptDetail,
  fetchPracticeAttemptHistory,
  fetchPracticeJson,
} from '../lib/practiceAttempts';
import { formatIeltsBand, ieltsBandFromAttempt } from '../lib/ieltsSpeaking';
import { IeltsSpeakingResultAdapter } from './PracticeResultAdapters';

const assignmentFor = (practice) => practice?.assignment || {};

export const ieltsQuizState = (practice = {}) => {
  const assignment = assignmentFor(practice);
  const targetBand = assignment.config?.target_band;
  const completed = practice.status === 'completed';
  let targetState = 'none';
  if (targetBand != null) {
    targetState = practice.status_reason === 'target_met'
      ? 'met'
      : completed
        ? 'not_met'
        : 'pending';
  }
  return { completed, targetBand, targetState };
};

const statusText = (practice, t) => {
  const state = ieltsQuizState(practice);
  return {
    completion: t(state.completed ? 'Completed' : 'Not completed'),
    target: state.targetState === 'met'
      ? t('Target reached')
      : state.targetState === 'not_met'
        ? t('Target not reached')
        : state.targetState === 'pending'
          ? t('Target not reached yet')
          : t('No target score'),
  };
};

const recordingAttempts = (attempts) => attempts.filter((attempt) => (
  attempt.recording_id || attempt.status === 'completed'
));

export default function AdminStudentIeltsQuizList({ classId, studentId }) {
  const { t, locale } = useI18n();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openAssignment, setOpenAssignment] = useState('');
  const [attemptsByAssignment, setAttemptsByAssignment] = useState({});
  const [attemptLoading, setAttemptLoading] = useState('');
  const [attemptError, setAttemptError] = useState({});
  const [openAttempt, setOpenAttempt] = useState('');
  const [detailsByAttempt, setDetailsByAttempt] = useState({});
  const [detailLoading, setDetailLoading] = useState('');
  const [detailError, setDetailError] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState('');
  const [deletingId, setDeletingId] = useState('');

  const load = useCallback(async () => {
    if (!classId || !studentId) {
      setPractices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchPracticeJson(
        `/api/admin/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}/practice-assignments`,
      );
      setPractices(Array.isArray(data.practices) ? data.practices : []);
    } catch (loadError) {
      setError(loadError.message || t('The IELTS speaking records could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [classId, studentId, t]);

  useEffect(() => { load(); }, [load]);

  const ieltsPractices = useMemo(() => practices.filter(
    (practice) => assignmentFor(practice).practice_type === 'ielts_speaking',
  ), [practices]);

  const toggleAssignment = async (practice) => {
    const assignmentId = assignmentFor(practice).id;
    if (!assignmentId) return;
    if (openAssignment === assignmentId) {
      setOpenAssignment('');
      return;
    }
    setOpenAssignment(assignmentId);
    if (attemptsByAssignment[assignmentId]) return;
    setAttemptLoading(assignmentId);
    setAttemptError((current) => ({ ...current, [assignmentId]: '' }));
    try {
      const page = await fetchPracticeAttemptHistory({
        assignmentId,
        viewer: 'admin',
        studentId,
        pageSize: 200,
      });
      setAttemptsByAssignment((current) => ({
        ...current,
        [assignmentId]: recordingAttempts(page.attempts || []),
      }));
    } catch (loadError) {
      setAttemptError((current) => ({
        ...current,
        [assignmentId]: loadError.message || t('The IELTS speaking recordings could not be loaded.'),
      }));
    } finally {
      setAttemptLoading('');
    }
  };

  const toggleAttempt = async (attempt) => {
    if (!attempt?.id) return;
    if (openAttempt === attempt.id) {
      setOpenAttempt('');
      return;
    }
    setOpenAttempt(attempt.id);
    if (detailsByAttempt[attempt.id]) return;
    setDetailLoading(attempt.id);
    setDetailError((current) => ({ ...current, [attempt.id]: '' }));
    try {
      const detail = await fetchPracticeAttemptDetail(attempt.id, 'admin');
      setDetailsByAttempt((current) => ({ ...current, [attempt.id]: detail }));
    } catch (loadError) {
      setDetailError((current) => ({
        ...current,
        [attempt.id]: loadError.message || t('The recording details could not be loaded.'),
      }));
    } finally {
      setDetailLoading('');
    }
  };

  const deletePractice = async (assignmentId) => {
    setDeletingId(assignmentId);
    setError('');
    try {
      await fetchPracticeJson(
        `/api/admin/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}/practice-assignments/${encodeURIComponent(assignmentId)}`,
        { method: 'DELETE' },
      );
      setPractices((current) => current.filter(
        (practice) => assignmentFor(practice).id !== assignmentId,
      ));
      setAttemptsByAssignment((current) => {
        const next = { ...current };
        delete next[assignmentId];
        return next;
      });
      setConfirmDeleteId('');
      if (openAssignment === assignmentId) setOpenAssignment('');
      setOpenAttempt('');
    } catch (deleteError) {
      setError(deleteError.message || t('The practice could not be deleted.'));
    } finally {
      setDeletingId('');
    }
  };

  if (loading) return <p className="py-4 text-sm text-gray-500">{t('Loading IELTS speaking quizzes...')}</p>;
  if (error) return <div className="border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>;
  if (!ieltsPractices.length) return <p className="py-4 text-sm text-gray-500">{t('No IELTS speaking quizzes yet.')}</p>;

  return (
    <div className="divide-y divide-gray-200 border-y border-gray-200">
      {ieltsPractices.map((practice) => {
        const assignment = assignmentFor(practice);
        const state = ieltsQuizState(practice);
        const labels = statusText(practice, t);
        const assignmentOpen = openAssignment === assignment.id;
        const confirmingDelete = confirmDeleteId === assignment.id;
        const attempts = attemptsByAssignment[assignment.id] || [];
        return (
          <div key={practice.id || assignment.id} className="py-4">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => toggleAssignment(practice)}
                className="flex min-w-0 flex-1 items-start justify-between gap-4 text-left"
                aria-expanded={assignmentOpen}
              >
                <span className="min-w-0">
                  <span className="block font-semibold text-gray-950" data-i18n-skip>{assignment.title || t('IELTS Speaking Practice')}</span>
                  <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                    <span>{t('Completion')}: <strong className={state.completed ? 'text-emerald-700' : 'text-amber-700'}>{labels.completion}</strong></span>
                    <span>{t('Goal')}: <strong className={state.targetState === 'met' ? 'text-emerald-700' : 'text-gray-700'}>{labels.target}</strong></span>
                    {state.targetBand != null && <span>{t('Target band')}: <strong data-i18n-skip>{formatIeltsBand(state.targetBand)}</strong></span>}
                    <span>{t('{count} recordings', { count: practice.attempt_count || 0 })}</span>
                  </span>
                </span>
                {assignmentOpen
                  ? <ChevronUpIcon className="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
                  : <ChevronDownIcon className="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />}
              </button>
              {!confirmingDelete && (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(assignment.id)}
                  className="inline-flex h-9 shrink-0 items-center gap-1 rounded border border-red-200 px-3 text-sm font-semibold text-red-700 hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  {t('Delete practice')}
                </button>
              )}
            </div>

            {confirmingDelete && (
              <div className="mt-3 flex flex-col justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-3 sm:flex-row sm:items-center">
                <p className="text-sm text-red-800">
                  {t('Delete this practice? Its recordings and attempts will also be permanently deleted. This cannot be undone.')}
                </p>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    disabled={deletingId === assignment.id}
                    onClick={() => setConfirmDeleteId('')}
                    className="h-9 rounded border border-gray-300 bg-white px-3 font-semibold text-gray-700 disabled:opacity-50"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={deletingId === assignment.id}
                    onClick={() => deletePractice(assignment.id)}
                    className="h-9 rounded bg-red-600 px-3 font-semibold text-white disabled:opacity-50"
                  >
                    {deletingId === assignment.id ? t('Deleting...') : t('Confirm delete')}
                  </button>
                </div>
              </div>
            )}

            {assignmentOpen && (
              <div className="mt-4 border-t border-gray-100 pt-3">
                {attemptLoading === assignment.id && <p className="text-sm text-gray-500">{t('Loading recordings...')}</p>}
                {attemptError[assignment.id] && <p className="text-sm text-rose-700">{attemptError[assignment.id]}</p>}
                {attemptLoading !== assignment.id && !attemptError[assignment.id] && attempts.length === 0 && (
                  <p className="text-sm text-gray-500">{t('No recordings yet.')}</p>
                )}
                {attempts.map((attempt, index) => {
                  const attemptOpen = openAttempt === attempt.id;
                  const band = ieltsBandFromAttempt(attempt);
                  const timestamp = attemptTimestamp(attempt);
                  const detail = detailsByAttempt[attempt.id];
                  return (
                    <div key={attempt.id} className="border-t border-gray-100 first:border-t-0">
                      <button
                        type="button"
                        onClick={() => toggleAttempt(attempt)}
                        className="flex w-full items-center justify-between gap-4 py-3 text-left"
                        aria-expanded={attemptOpen}
                      >
                        <span>
                          <span className="font-semibold text-gray-900">{t('Recording {number}', { number: attempt.attempt_no || attempts.length - index })}</span>
                          {timestamp && <span className="ml-3 text-sm text-gray-500" data-i18n-skip>{new Date(timestamp).toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en-US')}</span>}
                        </span>
                        <span className="flex items-center gap-3">
                          <strong className="text-indigo-800" data-i18n-skip>{band == null ? t('Score pending') : formatIeltsBand(band)}</strong>
                          {attemptOpen
                            ? <ChevronUpIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                            : <ChevronDownIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />}
                        </span>
                      </button>
                      {attemptOpen && (
                        <div className="border-t border-gray-100 bg-gray-50 p-4">
                          {detailLoading === attempt.id && <p className="text-sm text-gray-500">{t('Loading recording details...')}</p>}
                          {detailError[attempt.id] && <p className="text-sm text-rose-700">{detailError[attempt.id]}</p>}
                          {detail && (
                            <IeltsSpeakingResultAdapter
                              attempt={detail.attempt}
                              details={detail.details}
                              t={t}
                              viewer="admin"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
